/**
 * Chatbot message display & parsing utilities.
 * - Extract and clean LLM output (strip "Source: global" lines, inline source refs, orphan ".pdf)" remnants, trailing source-field instruction).
 */

import React from "react";
import { Link, Typography } from "@mui/material";
import { parseDocumentTitle } from "../../utils/parseDocumentTitle";

/** Matches a line that is only "Source: global|regional|local" (used per line). */
const SOURCE_LINE_RE = /^\s*[\*\s]*\*?Source\*?:?\s*(global|regional|local)\s*\*?\s*$/im;

/** Matches inline JSON source tags to remove: {"source": "..."} or {'source': '...'}. */
const INLINE_SOURCE_JSON_RE = /\s*\{\s*["']source["']\s*:\s*["'][^"']*["']\s*\}\s*/g;

/** Matches "(Source: ...)" — [^)]+ stops at first ")", so values with parens (e.g. "doc (v2).pdf") leave ".pdf)" which we strip with ORPHAN_PDF_Paren_RE. */
const INLINE_SOURCE_PAREN_RE = /\s*\(\s*Source\s*:\s*[^)]+\)\s*/gi;

/** Matches unquoted inline source refs: {source: filename.pdf} or {source: 1704919016_jaundice.pdf}. */
const INLINE_SOURCE_UNQUOTED_RE = /\s*\{\s*source\s*:\s*[^}]+}\s*/gi;

/** Orphan remnants: ".pdf)" or ".pdf, NEJM 2017;377:1456)" (citation can contain spaces). */
const ORPHAN_PDF_Paren_RE = /\s*\.pdf\s*(?:,\s*[^)]*)?\s*\)\s*\**\s*/g;

/** Stray " **" at end of line/string (markdown artifact). */
const ORPHAN_BOLD_END_RE = /\s+\*\*\s*$/gm;

/** Trailing prompt-leak line about JSON/source field. */
const SOURCE_FIELD_INSTRUCTION_RE = /\n?\s*Every item in the JSON response includes a\s+["']source["']\s+field set to\s+["']global["']\.?\s*$/im;

/**
 * Strip inline {"source": "..."}, "(Source: ...)", orphan ".pdf)" remnants, and trailing source-field instruction.
 */
export function stripInlineSourceRefs(text) {
  if (!text || typeof text !== "string") return text;
  return text
    .replace(INLINE_SOURCE_JSON_RE, " ")
    .replace(INLINE_SOURCE_PAREN_RE, " ")
    .replace(INLINE_SOURCE_UNQUOTED_RE, " ")
    .replace(ORPHAN_PDF_Paren_RE, " ")
    .replace(ORPHAN_BOLD_END_RE, " ")
    .replace(SOURCE_FIELD_INSTRUCTION_RE, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Matches **N. when not already at line start — used to put numbered sections on their own line. */
const NUMBERED_BOLD_SECTION_RE = /([^\n])\*\*(\d+\.)/g;

/** Matches " * " (space-asterisk-space) so list items start on a new line instead of run-on. */
const BULLET_MARKER_RE = / \* /g;

/** After sentence end or colon, put plain numbered items (e.g. "me: 1. How" or "? 1. How") on a new line. */
const PLAIN_NUMBERED_AFTER_SENTENCE_RE = /([.?:])\s+(\d+\.\s+)/g;

/** Markdown ATX header "##" — ensure it always starts a new paragraph (break before ##). */
const MARKDOWN_HEADER_RE = /\s+##(?=\s|$)/g;

/**
 * Strip "Source: global|regional|local" lines and all inline source refs (JSON and paren form) from response text.
 * Ensures bold numbered sections (e.g. **1. **2.) start on a new line for clearer display.
 * Treats "##" as a new-paragraph symbol so " . ## Initial Steps" becomes a paragraph break before "## Initial Steps".
 */
export function stripSourceLabelsFromResponse(text) {
  if (!text || typeof text !== "string") return text;
  let out = text
    .split("\n")
    .filter((line) => !SOURCE_LINE_RE.test(line.trim()))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  out = stripInlineSourceRefs(out);
  // Ensure "##" (markdown header) always starts a new paragraph
  out = out.replace(MARKDOWN_HEADER_RE, "\n\n##");
  // Put numbered bold sections on a new line (e.g. "...documents: **1. Initial" → "...documents:\n\n**1. Initial")
  out = out.replace(NUMBERED_BOLD_SECTION_RE, "$1\n\n**$2");
  // Put plain numbered list items on a new line (e.g. "? 1. How long" → "?\n\n1. How long")
  out = out.replace(PLAIN_NUMBERED_AFTER_SENTENCE_RE, "$1\n\n$2");
  // Put list items on their own line (e.g. "Safety * Safety First:" → "Safety\n\n* Safety First:")
  out = out.replace(BULLET_MARKER_RE, "\n\n* ");
  return out.replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Extract display text from LLM output. Handles array of { answer, source }, single { answer }, { content }/{ text }, or string.
 * Strips "*Source:* global" style lines from the result.
 */
export function extractContent(output) {
  if (output == null) return "";
  if (typeof output === "string") {
    const trimmed = output.trim();
    if ((trimmed.startsWith("{") && trimmed.includes('"response"')) || (trimmed.startsWith("{") && trimmed.includes("'response'"))) {
      try {
        const parsed = JSON.parse(output);
        if (parsed && typeof parsed.response === "string") return stripSourceLabelsFromResponse(parsed.response);
      } catch (_) { /* fall through to plain string */ }
    }
    return stripSourceLabelsFromResponse(output);
  }
  if (typeof output !== "object") return String(output);

  let raw = "";
  if (Array.isArray(output) && output.length > 0) {
    const parts = output
      .map((item) => (item && typeof item.answer === "string" ? item.answer : ""))
      .filter(Boolean);
    if (parts.length > 0) raw = parts.join("\n\n");
  } else if (typeof output.answer === "string") {
    raw = output.answer;
  } else if (typeof output.content === "string") {
    raw = output.content;
  } else if (typeof output.text === "string") {
    raw = output.text;
  } else if (typeof output.response === "string") {
    raw = output.response;
  }

  if (raw) return stripSourceLabelsFromResponse(raw);

  try {
    return JSON.stringify(output);
  } catch (_) {
    return String(output);
  }
}

/** Matches unquoted {source: value} to extract cited document identifiers. */
const CITED_SOURCE_RE = /\{\s*source\s*:\s*([^}]+)\}/gi;

/**
 * Extract cited source identifiers from raw LLM output (before stripping).
 * Used to prefer or limit "Source documents" to refs the model actually cited.
 * @param {*} output - Same shape as extractContent (string or object with response/answer/content/text)
 * @returns {string[]} Unique cited identifiers (e.g. "1704919016_evaluation-of-jaundice-in-adults.pdf")
 */
export function extractCitedSourceNames(output) {
  if (output == null) return [];
  let raw = "";
  if (typeof output === "string") {
    const trimmed = output.trim();
    if (trimmed.startsWith("{") && (trimmed.includes('"response"') || trimmed.includes("'response'"))) {
      try {
        const parsed = JSON.parse(output);
        if (parsed && typeof parsed.response === "string") raw = parsed.response;
        else raw = output;
      } catch (_) {
        raw = output;
      }
    } else {
      raw = output;
    }
  } else if (typeof output === "object") {
    if (typeof output.response === "string") raw = output.response;
    else if (typeof output.answer === "string") raw = output.answer;
    else if (typeof output.content === "string") raw = output.content;
    else if (typeof output.text === "string") raw = output.text;
  }
  if (!raw || typeof raw !== "string") return [];
  const names = [];
  let m;
  CITED_SOURCE_RE.lastIndex = 0;
  while ((m = CITED_SOURCE_RE.exec(raw)) !== null) {
    const name = (m[1] || "").trim();
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

/** Re-export for consumers that already import from chatbotMessageUtils. Implemented in utils/parseDocumentTitle.js to avoid circular deps. */
export { parseDocumentTitle };

/**
 * Pull user-visible text from a JSON array element: step/action, item, or citation-shaped { name, description, source }.
 * @param {unknown} el
 * @returns {string|null}
 */
function extractStepOrCitationText(el) {
  if (!el || typeof el !== "object") return null;
  if (typeof el.action === "string" && el.action.trim()) return el.action.trim();
  if (typeof el.item === "string" && el.item.trim()) return el.item.trim();

  const name = typeof el.name === "string" ? el.name.trim() : "";
  const desc = typeof el.description === "string" ? el.description.trim() : "";
  if (name && desc) return `${name} — ${desc}`;
  if (desc) return desc;
  if (name) return name;
  if (typeof el.source === "string" && el.source.trim()) return el.source.trim();
  return null;
}

/**
 * react-markdown sometimes passes plain objects through `children` (e.g. odd AST / embedded data). React cannot render those.
 * @param {unknown} children
 * @returns {unknown}
 */
export function sanitizeMarkdownChildren(children) {
  if (children == null || typeof children === "boolean") return children;
  if (Array.isArray(children)) {
    return children.map((c) => sanitizeMarkdownChildren(c));
  }
  if (typeof children === "object") {
    if (React.isValidElement(children)) return children;
    try {
      return JSON.stringify(children);
    } catch (e) {
      console.warn("sanitizeMarkdownChildren: stringify failed", e);
      return "[Content]";
    }
  }
  return children;
}

/**
 * If content is (or contains) a JSON array of step/action or item objects, return them for display as a list.
 * Handles ```json ... ``` blocks and raw JSON. Ignores "source" field.
 * Supports { step, action } (guidelines), { item } (summary/findings), and { name, description, source } (citations).
 * @param {string} content - Raw LLM response
 * @returns {Array<{ step: number|string, action: string }>|null} Parsed steps or null
 */
export function parseStepActionResponse(content) {
  if (!content || typeof content !== "string") return null;
  const trimmed = content.trim();
  let jsonStr = trimmed;
  const codeBlock = /^```(?:json)?\s*([\s\S]*?)```\s*$/m.exec(trimmed);
  if (codeBlock) jsonStr = codeBlock[1].trim();
  const arrayMatch = jsonStr.match(/\[[\s\S]*\]/);
  if (arrayMatch) jsonStr = arrayMatch[0];
  try {
    const arr = JSON.parse(jsonStr);
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const items = [];
    for (const el of arr) {
      if (!el || typeof el !== "object") continue;
      const text = extractStepOrCitationText(el);
      if (text) {
        items.push({
          step: el.step != null ? el.step : items.length + 1,
          action: text,
        });
      }
    }
    return items.length > 0 ? items : null;
  } catch (_) {
    return null;
  }
}

/** Matches inline "(Source: document name)" or "(Source: filename.pdf)" in LLM response text. */
const INLINE_SOURCE_RE = /\(\s*Source:\s*([^)]+)\)/g;

/**
 * Renders message content with "(Source: xxx)" and bare filenames replaced by links.
 * If onOpenPdf(url, displayTitle) is provided, links open the PDF in the caller's modal instead of a new tab.
 * Returns a string if no links needed, or an array of React nodes (text fragments + link components).
 * @param {string} content
 * @param {Array<{ documentName?: string, downloadURL?: string }>} sourcesUsed
 * @param {{ onOpenPdf?: (url: string, title: string) => void }} options
 */
export function contentWithSourceLinks(content, sourcesUsed, options = {}) {
  if (!content || typeof content !== "string") return content;
  const { onOpenPdf } = options;
  const sources = sourcesUsed || [];
  const byName = new Map();
  sources.forEach((s) => {
    if (s.documentName && s.downloadURL) {
      const name = s.documentName.trim();
      byName.set(name, { url: s.downloadURL, name });
      const parsed = parseDocumentTitle(name);
      if (parsed && parsed !== name) byName.set(parsed, { url: s.downloadURL, name });
    }
  });

  const matches = [];
  INLINE_SOURCE_RE.lastIndex = 0;
  let m;
  while ((m = INLINE_SOURCE_RE.exec(content)) !== null) {
    const rawName = m[1].trim();
    const found = byName.get(rawName) || byName.get(parseDocumentTitle(rawName));
    if (found) {
      matches.push({ start: m.index, end: m.index + m[0].length, found, display: parseDocumentTitle(rawName) || rawName });
    } else {
      matches.push({ start: m.index, end: m.index + m[0].length, remove: true });
    }
  }
  sources.forEach((s) => {
    const name = (s.documentName || "").trim();
    if (!name) return;
    let idx = content.indexOf(name);
    while (idx !== -1) {
      const inParen = matches.some((match) => idx >= match.start && idx + name.length <= match.end);
      if (!inParen) {
        const found = byName.get(name);
        if (found) {
          matches.push({
            start: idx,
            end: idx + name.length,
            found,
            display: parseDocumentTitle(name) || name,
          });
        }
      }
      idx = content.indexOf(name, idx + 1);
    }
  });
  matches.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const match of matches) {
    if (match.remove) {
      merged.push(match);
      continue;
    }
    const overlap = merged.some((x) => match.start < x.end && match.end > x.start);
    if (!overlap) merged.push(match);
  }

  const parts = [];
  let lastIndex = 0;
  let key = 0;
  for (const seg of merged) {
    if (seg.start > lastIndex) {
      parts.push(<React.Fragment key={`t-${key++}`}>{content.slice(lastIndex, seg.start)}</React.Fragment>);
    }
    if (seg.remove) {
      lastIndex = seg.end;
      continue;
    }
    if (seg.found) {
      const display = seg.display;
      const url = seg.found.url;
      if (onOpenPdf && typeof onOpenPdf === "function") {
        parts.push(
          <Typography
            key={`l-${key++}`}
            component="span"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenPdf(url, display);
            }}
            sx={{
              color: "primary.main",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "inherit",
              "&:hover": { opacity: 0.85 },
            }}
          >
            {display}
          </Typography>
        );
      } else {
        parts.push(
          <Link
            key={`l-${key++}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: "inherit" }}
          >
            {display}
          </Link>
        );
      }
    }
    lastIndex = seg.end;
  }
  if (lastIndex < content.length) {
    parts.push(<React.Fragment key={`t-${key++}`}>{content.slice(lastIndex)}</React.Fragment>);
  }
  if (parts.length === 0) {
    return content.replace(INLINE_SOURCE_RE, "").replace(/\s{2,}/g, " ").trim();
  }
  return parts;
}
